import {AxiosCall, CancelTokenSource} from '../axios';
import {serializeQueryParams} from './PaymentHistoryService';
import {ServiceSuccessResponseType} from './service-mappers/BaseMapper';
import {ArrayRows} from './service-mappers/ClinicServiceMapper';
import {
  ArrayResponse,
  optedOutParams
} from './service-mappers/CommonServiceMapper';

export const getAllPatientsForGroup = async (
  clinic_group_id: number | string,
  options: any,
  cancelToken?: CancelTokenSource
): Promise<ServiceSuccessResponseType<ArrayResponse<ArrayRows>>> => {
  const queryParams = serializeQueryParams(options);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/all_patients?${queryParams}`,
    cancelToken
  );
};

export const getSpecificPatientDetails = async (
  clinic_group_id: number,
  clinic_id: number | string,
  patient_id: number
): Promise<any> => {
  let params = '';
  if (clinic_id) {
    params += 'clinic_id=' + clinic_id;
  }

  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/patient/${patient_id}?${params}`
  );
};

export const getGuarantor = async (
  clinic_group_id: number | string,
  guarantor_id: number | string,
  clinic_id?: number | string
): Promise<any> => {
  let url = `/clinic-group/${clinic_group_id}/guarantor/${guarantor_id}`;
  if (clinic_id) {
    url = url + `?clinic_id=${clinic_id}`;
  }
  return AxiosCall.get(url);
};

export const getGuarantorPatientsAndDueAmount = async (
  clinic_group_id: number,
  guarantor_id: number
): Promise<any> => {
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/guarantor/${guarantor_id}/patients-and-due`
  );
};

export const createPatient = async (
  clinic_group_id: number | string,
  data: {
    clinic_id: number | string | null | undefined;
    guarantor: any;
    patients: any[];
  }
): Promise<any> => {
  return AxiosCall.post(`/clinic-group/${clinic_group_id}/patient`, data);
};

export const updatePatient = async (
  clinic_group_id: number | string,
  data: {
    clinic_id: number | string | null | undefined;
    guarantor: any;
    patients: any[];
  }
): Promise<any> => {
  return AxiosCall.put(`/clinic-group/${clinic_group_id}/patient/edit`, data);
};

export const getPatientRecordChanges = async (
  clinic_group_id: number | string,
  patient_id: number | string
): Promise<any> => {
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/patient/${patient_id}/record-changes`
  );
};

export const markPatientPostedToEmr = async (
  clinic_group_id: number | string,
  patient_id: number | string,
  payload: {account_number?: string}
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/patient/${patient_id}/post-to-emr`,
    payload
  );
};

export const markPatientUpdatedInEmr = async (
  clinic_group_id: number | string,
  patient_id: number | string
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/patient/${patient_id}/updated-in-emr`,
    {}
  );
};

export const markGuarantorUpdatedInEmr = async (
  clinic_group_id: number | string,
  guarantor_id: number | string
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/guarantor/${guarantor_id}/updated-in-emr`,
    {}
  );
};

export const markGuarantorPostedToEmr = async (
  clinic_group_id: number | string,
  guarantor_id: number | string,
  payload: {account_number?: string}
): Promise<any> => {
  return AxiosCall.put(
    `/clinic-group/${clinic_group_id}/guarantor/${guarantor_id}/post-to-emr`,
    payload
  );
};

export const createPatientAndPayValidation = async (
  clinic_group_id: any,
  data: {
    clinic_id: number | string | null | undefined;
    guarantor: any;
    patients: any[];
    amount: number;
  }
): Promise<any> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/patient/create-and-pay/validate`,
    data
  );
};

export const fetchPatientLastImportDate = async (
  clinic_group_id: any,
  clinic_id?: any
): Promise<any> => {
  let options = '?';
  if (clinic_id) {
    options += `clinic_id=${clinic_id}`;
  }
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/patient/import/last-import-date/${options}`
  );
};

export const createPatientCopay = async (
  clinic_group_id: any,
  payload: {
    clinic_id: string | number;
    guarantor_id: string | number;
    patients: [
      {
        patient_id: number | string;
        amount: number;
        description?: string;
      }
    ];
  }
): Promise<any> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/patient/copay`,
    payload
  );
};

export const getListOfPatientCoPays = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  patient_id: number | null
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/patient/${patient_id}/patient-copay-all`;
  return AxiosCall.get(url);
};

export const cancelPatientCoPays = async (
  clinic_group_id: number | string,
  clinic_id: number | string,
  payload: any
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/patient-copay/delete`;

  return AxiosCall.post(url, payload);
};

export const patientMiscChargeTypes = async (
  clinic_group_id: number | string,
  clinic_id: number | string
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/patient-misc-charge-types`;

  return AxiosCall.get(url);
};

export const updateGuarantorOptOutElectronicStatement = async (
  clinic_group_id: number | string,
  guarantor_id: number | string,
  clinic_id: number | string,
  payload: Array<optedOutParams>
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic-id/${clinic_id}/guarantor/${guarantor_id}/opt-out-notifications`;
  return AxiosCall.post(url, {data: payload});
};

export const getGuarantorNotifications = async (
  clinic_group_id: number | string,
  guarantor_id: number | string,
  clinic_id: number | string
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic-id/${clinic_id}/guarantor/${guarantor_id}/get-opt-out-notifications`;
  return AxiosCall.get(url);
};

export const getAllPatients = ({
  clinic_group_id,
  params
}: {
  clinic_group_id: number;
  params?: {
    clinic_id?: number;
    patient_name?: string;
  };
}): Promise<any> => {
  const queryParams = serializeQueryParams(params);
  return AxiosCall.get(
    `/clinic-group/${clinic_group_id}/patient/search/all${
      queryParams ? '?' + queryParams : ''
    }`
  );
};

export const sendMessageToPatients = ({
  clinic_group_id,
  clinic_id,
  payload
}: {
  clinic_group_id: number;
  clinic_id: number;
  payload: any;
}): Promise<ServiceSuccessResponseType<never>> => {
  return AxiosCall.post(
    `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/messages/send`,
    payload
  );
};

export const getPatientForms = async (
  clinic_group_id: number | string
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/forms`;
  return AxiosCall.get(url);
};

/** Request DOB and/or SSN; permission checked per field. field: 'dob' | 'ssn' | 'dob,ssn' */
export const fetchPatientSensitiveDetails = async (
  clinic_group_id: number | string,
  patient_id: number | string,
  field: 'dob' | 'ssn' | 'dob,ssn'
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/patient/${patient_id}/sensitive-details?field=${encodeURIComponent(
    field
  )}`;
  return AxiosCall.get(url);
};

export const fetchSessionKey = async (): Promise<any> => {
  const url = `/user/key`;
  return AxiosCall.get(url);
};
